/**
 * Best-effort clipboard write that REPORTS whether the text actually landed.
 *
 * It used to return nothing, and that was a real defect once a caller started
 * confirming the copy to the user: `CopyLinksPanel` shows "full link copied", and
 * on an insecure origin or a denied permission the write silently did nothing while
 * the confirmation appeared anyway. A false confirmation is worse than no
 * confirmation — for a copy link it means the owner believes they hold a live
 * capability they never captured, and the URL is unrecoverable once the tab closes.
 *
 * THE FIRE-AND-FORGET CONTRACT IS UNCHANGED, which is what lets the four existing
 * callers stay as they are (`SendCopyModal`, `AccountModal` ×2,
 * `GlowSpineControls` — all `onClick={() => copyToClipboard(…)}`, all discarding
 * the result). The returned promise **never rejects**: every failure path is caught
 * here and reported as `false`, so ignoring it cannot produce an unhandled
 * rejection. Callers that want to know now can `await` it; callers that don't are
 * unaffected.
 *
 * Three ways this fails, all of them answering `false` rather than throwing:
 *   - no `navigator` binding at all (SSR, a unit test in the `node` environment);
 *   - no `navigator.clipboard` (an insecure `http://` origin — the API is
 *     secure-context only, which is exactly the local-dev case);
 *   - `writeText` rejecting (permission denied, or a document without focus).
 *
 * `globalThis.navigator` rather than a bare `navigator`: optional chaining guards a
 * null value, NOT an undeclared identifier, so `navigator?.clipboard` would still
 * throw a ReferenceError where the binding does not exist. The try/catch stays
 * regardless — a sandboxed frame can throw from the property access itself.
 *
 * @param {string} v  the text to place on the clipboard
 * @returns {Promise<boolean>} true only if the write resolved
 */
export async function copyToClipboard(v) {
  try {
    const clip = globalThis.navigator?.clipboard;
    // The `typeof` half is REACHABLE but behaviourally equivalent to letting the
    // call throw — mutation-verified: dropping it still answers false for `{}`,
    // `{writeText: null}` and `{writeText: "nope"}`, because the catch below
    // absorbs the TypeError. Kept anyway, unlike the unreachable guards removed
    // elsewhere in this feature: this one is a legible presence check on an
    // external browser API, and the distinction worth preserving is
    // reachable-but-equivalent (fine to keep) versus unreachable (dead code).
    if (!clip || typeof clip.writeText !== "function") return false;
    await clip.writeText(v);
    return true;
  } catch {
    /* unsupported / denied / not focused */
    return false;
  }
}
